import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface MultiTranslateLoaderResource {
    prefix?: string;
    suffix?: string;
    optional?: boolean;
}

type TranslationDictionary = Record<string, unknown>;

export class MultiTranslateHttpLoader implements TranslateLoader {
    constructor(
        private readonly http: HttpClient,
        private readonly resources: MultiTranslateLoaderResource[] = []
    ) { }

    getTranslation(lang: string): Observable<TranslationDictionary> {
        if (!this.resources.length) {
            return of({});
        }

        return forkJoin(this.resources.map((resource) => this.loadResource(resource, lang))).pipe(
            map((segments) => segments.reduce<TranslationDictionary>((merged, current) => deepMerge(merged, current), {}))
        );
    }

    private loadResource(resource: MultiTranslateLoaderResource, lang: string): Observable<TranslationDictionary> {
        const prefix = resource.prefix ?? '';
        const suffix = resource.suffix ?? '';
        const url = `${prefix}${lang}${suffix}`;

        return this.http.get<TranslationDictionary>(url).pipe(
            catchError(() => of({}))
        );
    }
}

function deepMerge(target: TranslationDictionary, source: TranslationDictionary): TranslationDictionary {
    const output: TranslationDictionary = { ...target };

    Object.entries(source).forEach(([key, value]) => {
        const existing = output[key];

        if (isPlainObject(existing) && isPlainObject(value)) {
            output[key] = deepMerge(existing, value);
            return;
        }

        output[key] = value;
    });

    return output;
}

function isPlainObject(value: unknown): value is TranslationDictionary {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}
